import type { ChatProfile } from '@customTypes/chat';
import type { ClubItem } from '@customTypes/types';
import {
  Alert,
  AppShell,
  Button,
  Card,
  Center,
  Flex,
  Loader,
  Modal,
  Pagination,
  ScrollArea,
  Table,
  Text,
  TextInput,
  Title,
  Badge,
  Progress
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useProfileStore } from '@shared/store/profileStore.ts';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { useDeleteClubMutation } from '@/features/admin-panel/api/deleteClub/hook';
import { useGetClubsActivityListQuery } from '@/features/admin-panel/api/getClubsActivitiList/hook';
import {
  useCreateConversationMutation,
  useGetChatProfileQuery
} from '@/features/chat/api';
import { useChatStore } from '@/features/chat/store/chatStore';
import { useShowFullClubProfileQuery } from '@/features/work/api/show-full-club-profile';
import { ClubFullProfile } from '@/features/work/view/club-full-profile/ClubFullProfile';
import { useClubProfileProgressQuery } from '@/shared/api/requests/profile-progress/club';

// Child row component for per-club hooks
const AdminClubRow = ({
  club,
  descendingIndex,
  chatProfile,
  handleConversation,
  setCurrentClubId,
  openProfile,
  onDeleteClick
}: {
  club: ClubItem;
  descendingIndex: number;
  chatProfile: ChatProfile | null;
  handleConversation: (senderId: string, recipientId: string) => void;
  setCurrentClubId: (id: string) => void;
  openProfile: () => void;
  onDeleteClick: (id: string) => void;
}) => {
  const { data: fullProfile, isLoading: isStatusLoading } =
    useShowFullClubProfileQuery({ profileId: club.clubProfileId.toString() });
  const { data: progressData, isLoading: isProgressLoading } =
    useClubProfileProgressQuery({ profileId: club.clubProfileId.toString() });

  const { profile, currentSessionId } = useProfileStore();
  const { updateChatProfile } = useChatStore();

  const chatProfileQuery = useGetChatProfileQuery({
    profileType: profile?.role!,
    profileId: currentSessionId?.toString()!
  });

  useEffect(() => {
    if (chatProfileQuery.data?.data.chatProfileId) {
      updateChatProfile(chatProfileQuery.data?.data);
    }
  }, [chatProfileQuery.data?.data?.chatProfileId]);

  // Status logic: use isHiring from full profile if available, else default to false
  const isHiring = fullProfile?.data?.clubInfo?.isHiring ?? false;
  const status = isHiring ? 'Открыт' : 'Закрыт';
  const statusColor = isHiring ? 'green' : 'red';
  const progress = progressData?.data?.totalCompletion;
  return (
    <Table.Tr key={club.clubProfileId}>
      <Table.Td>{descendingIndex}</Table.Td>
      <Table.Td>{club.name ?? '-'}</Table.Td>
      <Table.Td>{club.phone ?? '-'}</Table.Td>
      <Table.Td>{club.email ?? '-'}</Table.Td>
      <Table.Td>{club.country ?? '-'}</Table.Td>
      <Table.Td
        style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 200 }}
      >
        {club.address ?? '-'}
      </Table.Td>
      <Table.Td>
        {isStatusLoading ? (
          <Loader size='xs' />
        ) : (
          <Badge color={statusColor} variant='filled'>
            {status}
          </Badge>
        )}
      </Table.Td>
      <Table.Td>
        {isProgressLoading ? (
          <Loader size='xs' />
        ) : typeof progress === 'number' ? (
          <Flex direction='column' gap={4}>
            <Text size='xs'>{progress}%</Text>
            <Progress radius='xl' size='sm' value={progress} />
          </Flex>
        ) : (
          '-'
        )}
      </Table.Td>
      <Table.Td>
        <Flex gap='xs'>
          <Button
            size='xs'
            variant='outline'
            onClick={() => {
              setCurrentClubId(club.clubProfileId.toString());
              openProfile();
            }}
          >
            Профиль
          </Button>
          <Button
            size='xs'
            variant='filled'
            onClick={() =>
              chatProfile &&
              handleConversation(
                chatProfile.chatProfileId.toString(),
                club.chatProfileId.toString()
              )
            }
          >
            Чат
          </Button>
          <Button
            color='red'
            size='xs'
            variant='outline'
            onClick={() => onDeleteClick(club.clubProfileId.toString())}
          >
            Удалить
          </Button>
        </Flex>
      </Table.Td>
    </Table.Tr>
  );
};

export const AdminClubsList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useGetClubsActivityListQuery({
    pageNumber: page.toString()
  });

  const totalCount =
    data?.data.totalCount ??
    (data?.data.totalPages ?? 0) * (data?.data.pageSize ?? 10);
  const { chatProfile } = useChatStore();
  const createConversation = useCreateConversationMutation();
  const deleteClub = useDeleteClubMutation();

  const [isProfileOpen, { open: openProfile, close: closeProfile }] =
    useDisclosure();
  const [currentClubId, setCurrentClubId] = useState<string | null>(null);

  const [isDeleteOpen, { open: openDelete, close: closeDelete }] =
    useDisclosure();
  const [deleteClubId, setDeleteClubId] = useState<string | null>(null);
  const [confirmInput, setConfirmInput] = useState('');

  const handleConversation = async (senderId: string, recipientId: string) => {
    const response = await createConversation.mutateAsync({
      params: {
        senderProfileId: senderId,
        recipientProfileId: recipientId,
        isDirect: true
      }
    });
    const convId = response.data.conversationId.toString();
    navigate({
      to: '/admin/chat/conversation/$conversationId',
      params: { conversationId: convId }
    });
  };

  const onDeleteClick = (clubId: string) => {
    setDeleteClubId(clubId);
    setConfirmInput('');
    openDelete();
  };

  const confirmDelete = async () => {
    if (!deleteClubId) {
      return;
    }
    await deleteClub.mutateAsync({ profileId: deleteClubId });
    closeDelete();
    refetch();
  };

  return (
    <AppShell.Main>
      {/* Club Profile Modal */}
      <Modal
        withCloseButton
        opened={isProfileOpen}
        overlayProps={{ blur: 4, opacity: 0.6 }}
        onClose={closeProfile}
      >
        {currentClubId && (
          <ScrollArea p='md'>
            <ClubFullProfile clubId={currentClubId} />
          </ScrollArea>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        withCloseButton
        opened={isDeleteOpen}
        overlayProps={{ blur: 4, opacity: 0.6 }}
        title='Удаление клуба'
        onClose={closeDelete}
      >
        <Text mb='sm'>
          Вы уверены, что хотите удалить этот клуб? Действие необратимо. Введите
          &quot;delete&quot; для подтверждения.
        </Text>
        <TextInput
          placeholder='Введите delete для подтверждения'
          value={confirmInput}
          onChange={(e) => setConfirmInput(e.currentTarget.value)}
        />
        <Flex gap='xs' justify='end' mt='md'>
          <Button variant='default' onClick={closeDelete}>
            Отмена
          </Button>
          <Button
            color='red'
            disabled={confirmInput !== 'delete'}
            onClick={confirmDelete}
          >
            Удалить
          </Button>
        </Flex>
      </Modal>

      {/* Header */}
      <Flex align='center' justify='space-between' mb='lg'>
        <Title order={2}>Клубы</Title>
      </Flex>

      {/* States: Loading, Error, Empty */}
      {isLoading && (
        <Center style={{ height: 200 }}>
          <Loader />
        </Center>
      )}
      {isError && (
        <Alert color='red' mb='md' title='Ошибка'>
          Не удалось загрузить список клубов.
        </Alert>
      )}
      {!isLoading && data?.data.items.length === 0 && (
        <Center>
          <Text color='dimmed'>Нет клубов для отображения.</Text>
        </Center>
      )}

      {/* Clubs Table */}
      {!isLoading && data && (
        <Card withBorder p='lg' radius='md' shadow='sm'>
          <ScrollArea>
            <Table highlightOnHover striped withColumnBorders withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>No</Table.Th>
                  <Table.Th>Название</Table.Th>
                  <Table.Th>Телефон</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Страна</Table.Th>
                  <Table.Th>Адрес</Table.Th>
                  <Table.Th>Набор</Table.Th>
                  <Table.Th>Прогресс профиля</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.data.items.map((club, index) => {
                  const descendingIndex =
                    totalCount -
                    ((page - 1) * (data.data.pageSize ?? 10) + index);
                  return (
                    <AdminClubRow
                      key={club.clubProfileId}
                      chatProfile={chatProfile}
                      club={club}
                      descendingIndex={descendingIndex}
                      handleConversation={handleConversation}
                      openProfile={openProfile}
                      setCurrentClubId={setCurrentClubId}
                      onDeleteClick={onDeleteClick}
                    />
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>

          <Flex justify='center' mt='md'>
            <Pagination
              boundaries={1}
              siblings={1}
              total={data.data.totalPages}
              value={page}
              onChange={setPage}
            />
          </Flex>
        </Card>
      )}
    </AppShell.Main>
  );
};
